/** Library page header, upload panel and the upload -> analyze -> chat strip. */
import React from 'react';
import { TouchableOpacity, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { Text } from '@/components/ui/text';
import { reviewDesk } from '@/design-system';
import { useTranslation } from '@/hooks/useTranslation';
import { webStyles } from '../libraryStyles';

export interface LibraryIntroProps {
  isPhone: boolean;
  isCompact: boolean;
  /** Dashboard upload deep link: highlight the panel instead of opening a picker (CHAT-24). */
  highlighted: boolean;
  uploading: boolean;
  onPickSession: () => void;
  onPickVoice: () => void;
  onPickText: () => void;
}

export function LibraryIntro({ isPhone, isCompact, highlighted, uploading, onPickSession, onPickVoice, onPickText }: LibraryIntroProps) {
  const { t } = useTranslation();
  return (
    <>
    <View style={[webStyles.pageHeader, isCompact && webStyles.pageHeaderCompact]}>
      <View>
        <Text style={webStyles.pageTitle}>{t('library.webTitle')}</Text>
        <Text style={webStyles.pageSubtitle}>
          {t('library.webSubtitle')}
        </Text>
      </View>
      <TouchableOpacity
        style={webStyles.primaryButton}
        onPress={onPickSession}
        disabled={uploading}
        accessibilityRole="button"
      >
        <MaterialIcons name="cloud-upload" size={20} color="#FFFFFF" />
        <Text className="text-white" style={webStyles.primaryButtonText}>{t('library.webPrimaryUpload')}</Text>
      </TouchableOpacity>
    </View>

    <View
      style={[webStyles.uploadPanel, highlighted && webStyles.uploadPanelHighlighted]}
      nativeID="library_upload_panel"
      testID="library_upload_panel"
    >
      <View style={webStyles.uploadCopy}>
        <View style={webStyles.uploadIconLarge}>
          <MaterialIcons name="cloud-upload" size={28} color={reviewDesk.accent} />
        </View>
        <Text style={webStyles.uploadTitle}>{t('library.webUploadTitle')}</Text>
        <Text style={webStyles.uploadText}>
          {t('library.webUploadBody')}
        </Text>
        {highlighted && (
          <Text style={webStyles.uploadPrompt} testID="library_upload_prompt">
            {t('chatLibrary.library.uploadPrompt')}
          </Text>
        )}
      </View>
      <View style={[webStyles.uploadActions, isPhone && webStyles.uploadActionsPhone]}>
        <TouchableOpacity
          style={[webStyles.uploadChoice, isPhone && webStyles.uploadChoicePhone]}
          onPress={onPickVoice}
          accessibilityRole="button"
        >
          <View style={webStyles.choiceIcon}>
            <MaterialIcons name="mic" size={28} color={reviewDesk.accent} />
          </View>
          <View style={webStyles.choiceText}>
            <Text style={webStyles.choiceTitle}>{t('library.webVoiceTitle')}</Text>
            <Text style={webStyles.choiceMeta}>{t('library.webVoiceMeta')}</Text>
          </View>
          <MaterialIcons name="chevron-right" size={24} color={reviewDesk.inkSoft} />
        </TouchableOpacity>
        <TouchableOpacity
          style={[webStyles.uploadChoice, isPhone && webStyles.uploadChoicePhone]}
          onPress={onPickText}
          accessibilityRole="button"
        >
          <View style={webStyles.choiceIcon}>
            <MaterialIcons name="description" size={28} color={reviewDesk.accent} />
          </View>
          <View style={webStyles.choiceText}>
            <Text style={webStyles.choiceTitle}>{t('library.webTextTitle')}</Text>
            <Text style={webStyles.choiceMeta}>{t('library.webTextMeta')}</Text>
          </View>
          <MaterialIcons name="chevron-right" size={24} color={reviewDesk.inkSoft} />
        </TouchableOpacity>
      </View>
    </View>

    <View style={[webStyles.flowStrip, isCompact && webStyles.flowStripCompact]}>
      {[
        ['file-upload', t('library.webFlowUploadTitle'), t('library.webFlowUploadCopy')],
        ['auto-awesome', t('library.webFlowAnalyzeTitle'), t('library.webFlowAnalyzeCopy')],
        ['forum', t('library.webFlowChatTitle'), t('library.webFlowChatCopy')],
      ].map(([icon, title, copy], index) => (
        <View key={title} style={[webStyles.flowStep, isCompact && webStyles.flowStepCompact]}>
          <View style={webStyles.stepNumber}><Text style={webStyles.stepNumberText}>{index + 1}</Text></View>
          <View style={webStyles.stepIcon}>
            <MaterialIcons name={icon as any} size={24} color={index === 0 ? reviewDesk.accent : reviewDesk.inkSoft} />
          </View>
          <View style={webStyles.stepText}>
            <Text style={webStyles.stepTitle}>{title}</Text>
            <Text style={webStyles.stepCopy}>{copy}</Text>
          </View>
        </View>
      ))}
    </View>
    </>
  );
}
